import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { connect, ConnectedProps } from "react-redux";
import { getStoreList } from "@/reducers/storeReducer/storeThunks";
import { SwaggerAPI } from "@/api/hunger.api";
import ReservationAnalysis, {
  ReservationAnalysisSearchOrderDailyCountApi,
} from "@/components/Feature/ReservationAnalysis";
import { HungerTabSet } from "@/components/General/Tab";
import { isNotSet } from "@/tools/format.checker";
import { formatDate, getDateTimeRangePayload } from "@/helper/dateHelper";
import { useAsync, useAsyncFn } from "react-use";
import {
  MgtDownloadGroupStoreSummaryReportReq,
  MgtStoreBillItemRankDto,
  MgtStoreBillPaymentTotalPriceDto,
  MgtStoreResourcePerformanceAnalyzeByBillTypeDto,
  MgtStoreResourcePerformanceAnalyzeDto,
  MgtStoreResourceProductAcquisitionAnalyzeDto,
  MgtStoreResourceProductAcquisitionItemAnalyzeDto,
  SearchDateTimeRange,
} from "@/api/swagger";
import Table, { Column } from "@/components/Feature/Table";
import { numberWithComma, truncateNumber } from "@/helper/formatter";
import Modal from "@/components/General/Modal/Modal";
import ConditionalFragment from "@/components/General/ConditionalFragment";
import { faTimes } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import ConsumerAgeDistributionAnalysis from "@/components/Feature/ConsumerAgeDistributionAnalysis";
import DashboardCard from "@/components/MainFlow/Dashboard/Feature/DashboardCard";
import useMediaQuery from "@/hooks/useMediaQuery";
import {
  PeriodDataPicker,
  PeriodSearchHandler,
} from "@/components/Feature/DatePicker";
import usePeriodSetter from "@/hooks/usePeriodSetter";
import { faFileChartLine } from "@fortawesome/pro-solid-svg-icons";
import { GetReportHelper } from "@/helper/GetReportHelper";
import { StoreSwitch } from "@/components/Feature/StoreSwitch";
import { hungerMoment } from "@/moment";

type Props = ReduxProps<typeof connector>;

const countCnt = (...dto: Array<{ cnt: number }>) => {
  return dto.reduce((prev, curr) => prev + curr.cnt, 0);
};
const countPrice = (
  ...dto: Array<
    | { price: number; totalPrice?: undefined | null }
    | { totalPrice: number; price?: undefined | null }
  >
) => {
  return dto.reduce((prev, curr) => prev + (curr.price ?? curr.totalPrice), 0);
};
const getRatio = (numerator: number, denominator: number) => {
  return (
    isNaN(numerator / denominator) ? 0 : (numerator / denominator) * 100
  ).toFixed(1);
};
const summaryPayment = (data: Array<MgtStoreBillPaymentTotalPriceDto>) => {
  const total = countPrice(...data);

  return data.map(({ paymentType, totalPrice }) => ({
    paymentType,
    ratio: `${getRatio(totalPrice, total)}%`,
    totalPrice,
  }));
};
const StoreDashboard: React.FC<Props> = ({ getStoreList, stores }) => {
  const [storeId, setStoreId] = useState<number | null>(null);

  const { value: wholeSummary } = useAsync(async () => {
    if (!storeId) return null;
    const { data } = await SwaggerAPI.getStoreSummary(storeId);

    return data;
  }, [storeId]);

  useEffect(() => {
    getStoreList();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const firstStoreId = stores[0]?.id;
    if (firstStoreId) {
      setStoreId(firstStoreId);
    }
    // eslint-disable-next-line
  }, [stores]);

  const searchOrderDailyCountApi =
    useCallback<ReservationAnalysisSearchOrderDailyCountApi>(
      (config) => SwaggerAPI.searchStoreOrderDailyCount(storeId!, config),
      [storeId]
    );

  if (isNotSet(storeId)) return null;

  return (
    <React.Fragment>
      <StoreSwitch
        className={"mb-6"}
        storeId={storeId}
        onStoreChange={setStoreId}
      />
      <ul className="dashboard__list">
        <DashboardCard
          title="累積訂單數"
          unit="件"
          count={wholeSummary?.totalOrderCount ?? 0}
          icon={["far", "receipt"]}
        />
        <DashboardCard
          title="累積總消費金額"
          unit="元"
          count={wholeSummary?.totalCheckoutPrice ?? 0}
          icon={["far", "sack-dollar"]}
          sign="$"
        />
      </ul>
      <div>
        <HungerTabSet
          tabs={["業績分析", "預約分析", "結帳分析", "請領分析", "報表下載"]}
        >
          <PerformanceAnalysis storeId={storeId} />
          <div>
            <ReservationAnalysis
              type={"bar"}
              searchOrderDailyCountApi={searchOrderDailyCountApi}
            />
            <div className={"mt-6"}>
              <HungerTabSet
                mode={"gather"}
                tabs={["指定分析", "熟客分析", "背景分析"]}
              >
                <ReservationResourceSpecifiedAnalysis storeId={storeId} />
                <ReservationResourceOrderConsumerAnalysis storeId={storeId} />
                <ReservationResourceConsumerBackgroundAnalysis
                  storeId={storeId}
                />
              </HungerTabSet>
            </div>
          </div>
          <PaymentAnalysis storeId={storeId} />
          <ResourceProductAcquisitionAnalyze storeId={storeId} />
          <Report storeId={storeId} />
        </HungerTabSet>
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = (state: RootState) => ({
  stores: state.store.list,
});

const mapDispatchToProps = {
  getStoreList,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

export default connector(StoreDashboard);

const PerformanceAnalysis: React.FC<{ storeId: number }> = ({ storeId }) => {
  const searchPeriod = useRef<{ from: string; until: string }>({
    from: "",
    until: "",
  });

  const [{ value: summaries = [] }, fetcher] = useAsyncFn(
    async (config: { time: SearchDateTimeRange }) => {
      const { data } = await SwaggerAPI.searchStoreResourcePerformanceAnalyze(
        storeId,
        config
      );
      return data;
    },
    [storeId]
  );

  const onSearch = useCallback<PeriodSearchHandler>(
    ({ startAt, endAt }) => {
      searchPeriod.current = getDateTimeRangePayload(startAt, endAt);

      fetcher({
        time: searchPeriod.current,
      });
    },
    [fetcher]
  );

  useEffect(() => {
    onSearch({
      startAt: hungerMoment().startOf("month"),
      endAt: hungerMoment(),
    });
  }, [storeId]);

  return (
    <Fragment>
      <PeriodDataPicker
        onSearch={onSearch}
        className={"mb-5 md:mb-6 p-5 bg-white rounded-xl"}
      />
      <div className={"bg-white rounded-xl"}>
        <div className={"divide-y divide-grey-4"}>
          {summaries.map((summary) => (
            <ResourcePerformanceAnalysis
              key={summary.resource.id}
              data={summary}
              searchPeriod={searchPeriod}
            />
          ))}
        </div>
      </div>
    </Fragment>
  );
};

type ResourcePerformanceAnalysisTableBillType =
  | MgtStoreResourcePerformanceAnalyzeByBillTypeDto["billType"]
  | "SUMMARY";

type ResourcePerformanceAnalysisData = (Pick<
  MgtStoreResourcePerformanceAnalyzeByBillTypeDto,
  "performanceSpecify" | "performanceUnSpecify"
> & {
  billType: ResourcePerformanceAnalysisTableBillType;
  total: number;
}) &
  (
    | {
        data: MgtStoreResourcePerformanceAnalyzeByBillTypeDto;
        resource: { name: string; id: number };
      }
    | {
        data?: undefined;
        resource?: undefined;
      }
  );

const ResourcePerformanceAnalysis: React.FC<{
  data: MgtStoreResourcePerformanceAnalyzeDto;
  searchPeriod: React.MutableRefObject<{ from?: string; until?: string }>;
}> = ({ data, searchPeriod }) => {
  const match = useMediaQuery("(max-width: 471px)");

  const tableData = useMemo<Array<ResourcePerformanceAnalysisData>>(() => {
    const serializer = (
      data: MgtStoreResourcePerformanceAnalyzeByBillTypeDto,
      resource: MgtStoreResourcePerformanceAnalyzeDto["resource"]
    ): ResourcePerformanceAnalysisData => {
      const { billType, performanceSpecify, performanceUnSpecify } = data;

      return {
        billType,
        performanceSpecify,
        performanceUnSpecify,
        total: performanceSpecify + performanceUnSpecify,
        data,
        resource,
      };
    };

    const result = [
      serializer(data.performanceOrder, data.resource),
      serializer(data.performanceProduct, data.resource),
      serializer(data.performanceVoucher, data.resource),
      serializer(data.performanceDeposit, data.resource),
    ];

    return result.concat({
      billType: "SUMMARY",
      performanceSpecify: result.reduce(
        (prev, curr) => prev + curr.performanceSpecify,
        0
      ),
      performanceUnSpecify: result.reduce(
        (prev, curr) => prev + curr.performanceUnSpecify,
        0
      ),
      total: result.reduce(
        (prev, curr) =>
          prev + curr.performanceUnSpecify + curr.performanceSpecify,
        0
      ),
    });
  }, [data]);

  const columns = useMemo<
    Array<Column<ResourcePerformanceAnalysisData>>
  >(() => {
    const billTypeName: Record<
      ResourcePerformanceAnalysisTableBillType,
      string
    > = {
      ORDER: "服務",
      PRODUCT: "商品",
      VOUCHER: "票券",
      DEPOSIT_POINT: "儲值金",
      SUMMARY: "合計",
    };
    return [
      {
        accessor: "billType",
        header: "項目",
        formatter: (data) => billTypeName[data.billType],
      },
      {
        accessor: "performanceSpecify",
        header: "指定業績",
        formatter: ({ performanceSpecify }) =>
          `${numberWithComma(performanceSpecify)}`,
      },
      {
        accessor: "performanceUnSpecify",
        header: "不指定業績",
        formatter: ({ performanceUnSpecify }) =>
          `${numberWithComma(performanceUnSpecify)}`,
      },
      {
        accessor: "total",
        header: "總業績",
        formatter: ({ total }) => `${numberWithComma(total)}`,
      },
      {
        accessor: "data",
        header: "操作",
        formatter: ({ data, resource }) =>
          data ? (
            <ResourcePerformanceAnalysisReview
              {...data}
              resource={resource!}
              searchPeriod={searchPeriod}
            />
          ) : (
            ""
          ),
        hidden: ({ data }) => isNotSet(data),
      },
    ];
  }, []);

  const totalProfit = useMemo(
    () =>
      tableData.reduce(
        (prev, curr) =>
          curr.billType === "SUMMARY" ? prev : prev + curr.total,
        0
      ),
    [tableData]
  );

  return (
    <div className={"mb-6"}>
      <div
        className={
          "px-5 py-3 md:px-10 md:py-5 border-b border-solid border-grey-4"
        }
      >
        <div>
          <p className={"text-2xl text-primary-pink font-semibold"}>
            {data.resource.name}
          </p>
          <p className={"text-grey-3"}>${numberWithComma(totalProfit)}</p>
        </div>
      </div>
      <Table
        lastRowClassName={match ? undefined : "font-medium"}
        data={tableData}
        columns={columns}
      />
    </div>
  );
};

const ResourcePerformanceAnalysisReview: React.FC<
  MgtStoreResourcePerformanceAnalyzeByBillTypeDto & {
    resource: { name: string; id: number };
    searchPeriod: React.MutableRefObject<{ from?: string; until?: string }>;
  }
> = ({ resource, searchPeriod, ...data }) => {
  const [open, setOpen] = useState<boolean>(false);

  const billTypeLabel = useRef<
    Record<MgtStoreResourcePerformanceAnalyzeByBillTypeDto["billType"], any>
  >({
    ORDER: "服務",
    PRODUCT: "商品",
    VOUCHER: "票券",
    DEPOSIT_POINT: "儲值金",
  });

  const toggle = useCallback(() => setOpen((prev) => !prev), []);

  const paymentMethodSummary = useMemo(
    () => summaryPayment(data.billPrices),
    [data.billPrices]
  );

  const paymentTableColumns = useMemo<
    Array<Column<ReturnType<typeof summaryPayment>[number]>>
  >(
    () => [
      {
        accessor: "paymentType",
        header: "支付類型",
      },
      {
        accessor: "ratio",
        header: "比例",
      },
      {
        accessor: "totalPrice",
        header: "金額(元)",
        formatter: ({ totalPrice }) => numberWithComma(totalPrice),
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <button onClick={toggle} type={"button"} className={"text-primary-pink"}>
        查看
      </button>
      <ConditionalFragment condition={open}>
        <Modal className={"md:py-12"}>
          <div
            className={
              "flex flex-col items-stretch w-full max-w-[40rem] h-full bg-grey-5 md:rounded-xl md:overflow-hidden"
            }
          >
            <div className={"flex flex-col px-6 py-4"}>
              <button
                onClick={toggle}
                type={"button"}
                className={"flex items-center justify-center self-end w-6 h-6"}
              >
                <FontAwesomeIcon
                  icon={faTimes}
                  className={"text-2xl text-grey-3"}
                />
              </button>
              <p className={"mb-1 text-2xl font-semibold"}>
                {billTypeLabel.current[data.billType]}
              </p>
              <div className={"md:flex"}>
                <p
                  className={
                    "mb-1 md:mb-0 md:flex-1 text-sm text-grey-2 font-medium"
                  }
                >
                  {resource.name}
                </p>
                <p className={"text-sm text-grey-2 font-medium"}>
                  {formatDate(
                    new Date(searchPeriod.current.from ?? Date.now()),
                    "YYYY/MM/DD"
                  )}{" "}
                  ~{" "}
                  {formatDate(
                    new Date(searchPeriod.current.until ?? Date.now()),
                    "YYYY/MM/DD"
                  )}
                </p>
              </div>
            </div>
            <div className={"flex-1 overflow-scroll"}>
              <ConditionalFragment condition={data.billType === "ORDER"}>
                <HungerTabSet
                  mode={"gather"}
                  sticky
                  tabs={["來源分析", "服務排行", "支付方式"]}
                >
                  <ResourceServicePerformanceAnalysis {...data} />
                  <ResourceBillTypeRankingAnalysis {...data} withTabs />
                  <Table
                    data={paymentMethodSummary}
                    columns={paymentTableColumns}
                  />
                </HungerTabSet>
              </ConditionalFragment>
              <ConditionalFragment condition={data.billType === "PRODUCT"}>
                <div className={"bg-white"}>
                  <Header>項目排行</Header>
                  <ResourceBillTypeRankingAnalysis {...data} />
                </div>
              </ConditionalFragment>
              <ConditionalFragment condition={data.billType === "VOUCHER"}>
                <div className={"bg-white"}>
                  <Header>項目排行</Header>
                  <ResourceBillTypeRankingAnalysis {...data} />
                </div>
              </ConditionalFragment>
              <ConditionalFragment
                condition={data.billType === "DEPOSIT_POINT"}
              >
                <div className={"bg-white"}>
                  <Header>項目排行</Header>
                  <ResourceBillTypeRankingAnalysis {...data} />
                </div>
              </ConditionalFragment>
            </div>
          </div>
        </Modal>
      </ConditionalFragment>
    </React.Fragment>
  );
};

type ResourceServiceOriginalAnalysisOrderType =
  | "SPECIFY"
  | "UN_SPECIFY"
  | "TOTAL";
type ResourceServiceOriginalAnalysisConsumerType =
  | "NEW"
  | "SECOND"
  | "COMMON"
  | "TOTAL";
type ResourceServiceOriginalAnalysisGenderType =
  | "FEMALE"
  | "MALE"
  | "OTHER"
  | "TOTAL";

interface ResourceServiceOriginalAnalysisData<T extends string> {
  type: T;
  summary: string;
  price: string;
}

const ResourceServicePerformanceAnalysis: React.FC<MgtStoreResourcePerformanceAnalyzeByBillTypeDto> =
  (props) => {
    const specifyAnalysisData = useMemo<
      Array<
        ResourceServiceOriginalAnalysisData<ResourceServiceOriginalAnalysisOrderType>
      >
    >(() => {
      const { specifyOrders, unSpecifyOrders } = props;
      const totalOrderCount = countCnt(specifyOrders, unSpecifyOrders);
      const totalOrderPrice = countPrice(specifyOrders, unSpecifyOrders);

      return [
        {
          type: "SPECIFY",
          summary: `${specifyOrders.cnt} (${getRatio(
            specifyOrders.cnt,
            totalOrderCount
          )}%)`,
          price: numberWithComma(specifyOrders.price),
        },
        {
          type: "UN_SPECIFY",
          summary: `${unSpecifyOrders.cnt} (${getRatio(
            unSpecifyOrders.cnt,
            totalOrderCount
          )}%)`,
          price: numberWithComma(unSpecifyOrders.price),
        },
        {
          type: "TOTAL",
          summary: `${unSpecifyOrders.cnt + specifyOrders.cnt} (100%)`,
          price: numberWithComma(totalOrderPrice),
        },
      ];
    }, [props]);

    const specifyConsumerAnalysisData = useMemo<
      Array<
        ResourceServiceOriginalAnalysisData<ResourceServiceOriginalAnalysisConsumerType>
      >
    >(() => {
      const {
        firstTimeConsumerOrders,
        secondTimeConsumerOrders,
        oldConsumerOrders,
      } = props;
      const totalOrderCount = countCnt(
        firstTimeConsumerOrders,
        secondTimeConsumerOrders,
        oldConsumerOrders
      );
      const totalOrderPrice = countPrice(
        firstTimeConsumerOrders,
        secondTimeConsumerOrders,
        oldConsumerOrders
      );

      return [
        {
          type: "COMMON",
          summary: `${oldConsumerOrders.cnt} (${getRatio(
            oldConsumerOrders.cnt,
            totalOrderCount
          )}%)`,
          price: numberWithComma(oldConsumerOrders.price),
        },
        {
          type: "SECOND",
          summary: `${secondTimeConsumerOrders.cnt} (${getRatio(
            secondTimeConsumerOrders.cnt,
            totalOrderCount
          )}%)`,
          price: numberWithComma(secondTimeConsumerOrders.price),
        },
        {
          type: "NEW",
          summary: `${firstTimeConsumerOrders.cnt} (${getRatio(
            firstTimeConsumerOrders.cnt,
            totalOrderCount
          )}%)`,
          price: numberWithComma(firstTimeConsumerOrders.price),
        },
        {
          type: "TOTAL",
          summary: `${
            firstTimeConsumerOrders.cnt +
            secondTimeConsumerOrders.cnt +
            oldConsumerOrders.cnt
          } (100%)`,
          price: numberWithComma(totalOrderPrice),
        },
      ];
    }, [props]);

    const specifyGenderAnalysisData = useMemo<
      Array<
        ResourceServiceOriginalAnalysisData<ResourceServiceOriginalAnalysisGenderType>
      >
    >(() => {
      const {
        genderFemaleConsumerOrders,
        genderMaleConsumerOrders,
        genderOtherConsumerOrders,
      } = props;
      const totalOrderCount = countCnt(
        genderFemaleConsumerOrders,
        genderMaleConsumerOrders,
        genderOtherConsumerOrders
      );
      const totalOrderPrice = countPrice(
        genderFemaleConsumerOrders,
        genderMaleConsumerOrders,
        genderOtherConsumerOrders
      );

      return [
        {
          type: "FEMALE",
          summary: `${genderFemaleConsumerOrders.cnt} (${getRatio(
            genderFemaleConsumerOrders.cnt,
            totalOrderCount
          )}%)`,
          price: numberWithComma(genderFemaleConsumerOrders.price),
        },
        {
          type: "MALE",
          summary: `${genderMaleConsumerOrders.cnt} (${getRatio(
            genderMaleConsumerOrders.cnt,
            totalOrderCount
          )}%)`,
          price: numberWithComma(genderMaleConsumerOrders.price),
        },
        {
          type: "OTHER",
          summary: `${genderOtherConsumerOrders.cnt} (${getRatio(
            genderOtherConsumerOrders.cnt,
            totalOrderCount
          )}%)`,
          price: numberWithComma(genderOtherConsumerOrders.price),
        },
        {
          type: "TOTAL",
          summary: `${totalOrderCount} (100%)`,
          price: numberWithComma(totalOrderPrice),
        },
      ];
    }, [props]);

    const specifyAnalysisColumns = useMemo<
      Array<
        Column<
          ResourceServiceOriginalAnalysisData<ResourceServiceOriginalAnalysisOrderType>
        >
      >
    >(() => {
      const typeLabel: Record<
        ResourceServiceOriginalAnalysisData<ResourceServiceOriginalAnalysisOrderType>["type"],
        string
      > = {
        SPECIFY: "指定",
        UN_SPECIFY: "不指定",
        TOTAL: "合計",
      };

      return [
        {
          accessor: "type",
          header: "預約類型",
          formatter: ({ type }) => typeLabel[type],
        },
        {
          accessor: "summary",
          header: "單數(佔比)",
        },
        {
          accessor: "price",
          header: "金額(元)",
        },
      ];
    }, []);

    const specifyConsumerAnalysisColumns = useMemo<
      Array<
        Column<
          ResourceServiceOriginalAnalysisData<ResourceServiceOriginalAnalysisConsumerType>
        >
      >
    >(() => {
      const typeLabel: Record<
        ResourceServiceOriginalAnalysisData<ResourceServiceOriginalAnalysisConsumerType>["type"],
        string
      > = {
        COMMON: "舊客：3次+",
        SECOND: "舊客：2次",
        NEW: "新客：首次來訪",
        TOTAL: "合計",
      };

      return [
        {
          accessor: "type",
          header: "舊客類型",
          formatter: ({ type }) => typeLabel[type],
        },
        {
          accessor: "summary",
          header: "單數(佔比)",
        },
        {
          accessor: "price",
          header: "金額(元)",
        },
      ];
    }, []);
    const specifyGenderAnalysisColumns = useMemo<
      Array<
        Column<
          ResourceServiceOriginalAnalysisData<ResourceServiceOriginalAnalysisGenderType>
        >
      >
    >(() => {
      const typeLabel: Record<
        ResourceServiceOriginalAnalysisData<ResourceServiceOriginalAnalysisGenderType>["type"],
        string
      > = {
        FEMALE: "女性",
        MALE: "男性",
        OTHER: "其他",
        TOTAL: "合計",
      };

      return [
        {
          accessor: "type",
          header: "性別",
          formatter: ({ type }) => typeLabel[type],
        },
        {
          accessor: "summary",
          header: "單數(佔比)",
        },
        {
          accessor: "price",
          header: "金額(元)",
        },
      ];
    }, []);

    return (
      <div className={"divide-y divide-grey-4"}>
        <div>
          <div className={"px-5 md:px-10 py-3 md:py-4 mb-4"}>
            <div className={"text-2xl font-semibold text-primary-pink"}>
              指定分析
            </div>
          </div>
          <Table
            stickyOffsetClassName={"md:-top-6"}
            data={specifyAnalysisData}
            columns={specifyAnalysisColumns}
          />
        </div>
        <div>
          <div className={"px-5 md:px-10 py-3 md:py-4 mb-4"}>
            <div className={"text-2xl font-semibold text-primary-pink"}>
              舊客分析
            </div>
          </div>
          <Table
            stickyOffsetClassName={"md:-top-6"}
            data={specifyConsumerAnalysisData}
            columns={specifyConsumerAnalysisColumns}
          />
        </div>
        <div>
          <div className={"px-5 md:px-10 py-3 md:py-4"}>
            <div className={"text-2xl font-semibold text-primary-pink"}>
              性別分析
            </div>
          </div>
          <Table
            stickyOffsetClassName={"md:-top-6"}
            data={specifyGenderAnalysisData}
            columns={specifyGenderAnalysisColumns}
          />
        </div>
      </div>
    );
  };

const ResourceBillTypeRankingAnalysis: React.FC<
  MgtStoreResourcePerformanceAnalyzeByBillTypeDto & { withTabs?: boolean }
> = ({ withTabs, ...props }) => {
  return (
    <div>
      <Table
        stickyOffsetClassName={withTabs ? "md:-top-6" : "md:top-0"}
        showEmpty
        data={props.itemRanks}
        columns={[
          {
            accessor: "item",
            header:
              props.billType === "PRODUCT" ||
              props.billType === "VOUCHER" ||
              props.billType === "DEPOSIT_POINT"
                ? "項目"
                : "服務項目",
          },
          {
            accessor: "billTimes",
            header:
              props.billType === "PRODUCT" || props.billType === "VOUCHER"
                ? "數量"
                : "次數",
          },
          {
            accessor: "totalPrice",
            header: "預估金額(元)",
            formatter: ({ totalPrice }) => numberWithComma(totalPrice),
          },
        ]}
      />
    </div>
  );
};

const ReservationResourceSpecifiedAnalysis: React.FC<{ storeId: number }> = ({
  storeId,
}) => {
  interface Data {
    resource: string;
    thisMonthSpecifyOrderRatio: string;
    lastMonthSpecifyOrderRatio: string;
    growthRate: number;
  }

  const { value = [] } = useAsync(async () => {
    const { data } = await SwaggerAPI.getStoreResourceOrderAnalyze(storeId);

    const result: Array<Data> = data.map(
      ({
        resource,
        thisMonthSpecifyOrderCnt,
        thisMonthTotalOrderCnt,
        lastMonthSpecifyOrderCnt,
        lastMonthTotalOrderCnt,
      }) => {
        const thisMonthSpecifyOrderRatio = getRatio(
          thisMonthSpecifyOrderCnt,
          thisMonthTotalOrderCnt
        );
        const lastMonthSpecifyOrderRatio = getRatio(
          lastMonthSpecifyOrderCnt,
          lastMonthTotalOrderCnt
        );

        return {
          resource: resource?.name ?? "本店綜合",
          thisMonthSpecifyOrderRatio,
          lastMonthSpecifyOrderRatio,
          growthRate:
            parseFloat(thisMonthSpecifyOrderRatio) -
            parseFloat(lastMonthSpecifyOrderRatio),
        };
      }
    );
    return result;
  }, [storeId]);

  const columns = useMemo<Array<Column<Data>>>(
    () => [
      {
        accessor: "resource",
        header: "資源",
        flex: 3,
      },
      {
        accessor: "thisMonthSpecifyOrderRatio",
        header: "本月指定率",
        formatter: ({ thisMonthSpecifyOrderRatio }) =>
          `${thisMonthSpecifyOrderRatio}%`,
      },
      {
        accessor: "lastMonthSpecifyOrderRatio",
        header: "上月指定率",
        formatter: ({ lastMonthSpecifyOrderRatio }) =>
          `${lastMonthSpecifyOrderRatio}%`,
      },
      {
        accessor: "growthRate",
        header: "成長比例",
        formatter: ({ growthRate }) => (
          <span
            className={classNames({
              "text-green": growthRate >= 0,
              "text-red": growthRate < 0,
            })}
          >{`${growthRate.toFixed(1)}%`}</span>
        ),
      },
    ],
    []
  );

  return <Table data={value} columns={columns} />;
};

const ReservationResourceOrderConsumerAnalysis: React.FC<{ storeId: number }> =
  ({ storeId }) => {
    interface Data {
      resource: string;
      thisMonthOldConsumerOrderRatio: string;
      lastMonthOldConsumerOrderRatio: string;
      growthRate: number;
    }

    const { value = [] } = useAsync(async () => {
      const { data } = await SwaggerAPI.getStoreResourceOrderAnalyze(storeId);

      const result: Array<Data> = data.map(
        ({
          resource,
          thisMonthOldConsumerOrderCnt,
          thisMonthTotalOrderCnt,
          lastMonthOldConsumerOrderCnt,
          lastMonthTotalOrderCnt,
        }) => {
          const thisMonthOldConsumerOrderRatio = getRatio(
            thisMonthOldConsumerOrderCnt,
            thisMonthTotalOrderCnt
          );
          const lastMonthOldConsumerOrderRatio = getRatio(
            lastMonthOldConsumerOrderCnt,
            lastMonthTotalOrderCnt
          );

          return {
            resource: resource?.name ?? "本店綜合",
            thisMonthOldConsumerOrderRatio,
            lastMonthOldConsumerOrderRatio,
            growthRate:
              parseFloat(thisMonthOldConsumerOrderRatio) -
              parseFloat(lastMonthOldConsumerOrderRatio),
          };
        }
      );
      return result;
    }, [storeId]);

    const columns = useMemo<Array<Column<Data>>>(
      () => [
        {
          accessor: "resource",
          header: "資源",
          flex: 3,
        },
        {
          accessor: "thisMonthOldConsumerOrderRatio",
          header: "本月舊客率",
          formatter: ({ thisMonthOldConsumerOrderRatio }) =>
            `${thisMonthOldConsumerOrderRatio}%`,
        },
        {
          accessor: "lastMonthOldConsumerOrderRatio",
          header: "上月舊客率",
          formatter: ({ lastMonthOldConsumerOrderRatio }) =>
            `${lastMonthOldConsumerOrderRatio}%`,
        },
        {
          accessor: "growthRate",
          header: "成長比例",
          formatter: ({ growthRate }) => (
            <span
              className={classNames({
                "text-green": growthRate >= 0,
                "text-red": growthRate < 0,
              })}
            >{`${growthRate.toFixed(1)}%`}</span>
          ),
        },
      ],
      []
    );

    return <Table data={value} columns={columns} />;
  };

const ReservationResourceConsumerBackgroundAnalysis: React.FC<{
  storeId: number;
}> = ({ storeId }) => {
  const { value = [] } = useAsync(async () => {
    const { data } = await SwaggerAPI.getStoreConsumerBackgroundAnalyze(
      storeId
    );

    return data;
  }, [storeId]);

  return <ConsumerAgeDistributionAnalysis data={value} />;
};

const Header: React.FC = ({ children }) => {
  return (
    <div
      className={
        "px-5 md:px-10 py-5 text-primary-pink border-b border-solid border-grey-4"
      }
    >
      <p className={"text-2xl font-semibold"}>{children}</p>
    </div>
  );
};

const PaymentAnalysis: React.FC<{
  storeId: number;
}> = ({ storeId }) => {
  const [{ value }, fetcher] = useAsyncFn(
    async (config: { time: SearchDateTimeRange }) => {
      const { data } = await SwaggerAPI.searchStoreBillAnalyze(storeId, config);
      return data;
    },
    [storeId]
  );

  const onSearch = useCallback<PeriodSearchHandler>(
    ({ startAt, endAt }) => {
      fetcher({
        time: getDateTimeRangePayload(startAt, endAt),
      });
    },
    [fetcher]
  );

  useEffect(() => {
    onSearch({
      startAt: hungerMoment().startOf("month"),
      endAt: hungerMoment(),
    });
  }, [storeId]);

  const paymentTableColumns = useMemo<
    Array<Column<ReturnType<typeof summaryPayment>[number]>>
  >(
    () => [
      {
        accessor: "paymentType",
        header: "支付類型",
      },
      {
        accessor: "ratio",
        header: "比例",
      },
      {
        accessor: "totalPrice",
        header: "金額(元)",
        formatter: ({ totalPrice }) => numberWithComma(totalPrice),
      },
    ],
    []
  );

  const rankingTableColumns = useMemo<Array<Column<MgtStoreBillItemRankDto>>>(
    () => [
      {
        accessor: "item",
        header: "服務項目",
      },
      {
        accessor: "billTimes",
        header: "次數",
      },
      {
        accessor: "totalPrice",
        header: "金額(元)",
        formatter: ({ totalPrice }) => numberWithComma(totalPrice),
      },
    ],
    []
  );

  const overview = useMemo(() => {
    return !value ? [] : summaryPayment(value.billPricesAll);
  }, [value]);

  const servicePayment = useMemo(() => {
    return !value ? [] : summaryPayment(value.billPricesOrder);
  }, [value]);

  const productPayment = useMemo(() => {
    return !value ? [] : summaryPayment(value.billPricesProduct);
  }, [value]);

  const depositPayment = useMemo(() => {
    return !value ? [] : summaryPayment(value.billPricesDeposit);
  }, [value]);

  const voucherPayment = useMemo(() => {
    return !value ? [] : summaryPayment(value.billPricesVoucher);
  }, [value]);

  return (
    <Fragment>
      <PeriodDataPicker
        onSearch={onSearch}
        className={"mb-5 md:mb-6 p-5 bg-white rounded-xl"}
      />
      <HungerTabSet
        mode={"gather"}
        tabs={["總覽", "服務結帳", "商品結帳", "儲值金結帳", "票券結帳"]}
      >
        <div>
          <Header>支付方式</Header>
          <Table data={overview} columns={paymentTableColumns} />
        </div>
        <div>
          <div className={"mb-6"}>
            <Header>支付方式</Header>
            <Table data={servicePayment} columns={paymentTableColumns} />
          </div>
          <div>
            <Header>項目排行</Header>
            <Table
              showEmpty
              data={value?.billItemRanksOrder ?? []}
              columns={rankingTableColumns}
            />
          </div>
        </div>
        <div>
          <div className={"mb-6"}>
            <Header>支付方式</Header>
            <Table data={productPayment} columns={paymentTableColumns} />
          </div>
          <div>
            <Header>項目排行</Header>
            <Table
              showEmpty
              data={value?.billItemRanksProduct ?? []}
              columns={rankingTableColumns}
            />
          </div>
        </div>
        <div>
          <div className={"mb-6"}>
            <Header>支付方式</Header>
            <Table data={depositPayment} columns={paymentTableColumns} />
          </div>
        </div>
        <div>
          <div className={"mb-6"}>
            <Header>支付方式</Header>
            <Table data={voucherPayment} columns={paymentTableColumns} />
          </div>
          <div>
            <Header>項目排行</Header>
            <Table
              showEmpty
              data={value?.billItemRanksVoucher ?? []}
              columns={rankingTableColumns}
            />
          </div>
        </div>
      </HungerTabSet>
    </Fragment>
  );
};

const ResourceProductAcquisitionAnalyze: React.FC<{ storeId: number }> = ({
  storeId,
}) => {
  const searchPeriod = useRef<{ from: string; until: string }>({
    from: "",
    until: "",
  });

  const [{ value: summaries = [] }, fetcher] = useAsyncFn(
    async (config: { time: SearchDateTimeRange }) => {
      const { data } =
        await SwaggerAPI.searchStoreResourceProductAcquisitionAnalyze(
          storeId,
          config
        );
      return data;
    },
    [storeId]
  );

  const onSearch = useCallback<PeriodSearchHandler>(
    ({ startAt, endAt }) => {
      searchPeriod.current = getDateTimeRangePayload(startAt, endAt);

      fetcher({
        time: searchPeriod.current,
      });
    },
    [fetcher]
  );

  useEffect(() => {
    onSearch({
      startAt: hungerMoment().startOf("month"),
      endAt: hungerMoment(),
    });
  }, [storeId]);

  const columns = useMemo<
    Array<Column<MgtStoreResourceProductAcquisitionAnalyzeDto>>
  >(() => {
    return [
      {
        accessor: "resource",
        header: "設計師",
        formatter: ({ resource }) => resource?.name ?? "-",
        flex: 2,
      },
      {
        accessor: "sum",
        header: "總金額",
        formatter: ({ sum }) => truncateNumber(sum),
      },
      {
        accessor: "productItem",
        header: "操作",
        formatter: ({ productItem, resource }) => (
          <CheckResourceProductAcquisitionDetail
            data={productItem}
            resourceName={resource.name}
            searchPeriod={searchPeriod}
          />
        ),
      },
    ];
  }, [summaries]);

  return (
    <React.Fragment>
      <PeriodDataPicker
        onSearch={onSearch}
        className={"mb-5 md:mb-6 p-5 bg-white rounded-xl"}
      />
      <div className={"bg-white rounded-xl"}>
        <div className={"divide-y divide-grey-4"}>
          <Table data={summaries} columns={columns} />
        </div>
      </div>
    </React.Fragment>
  );
};

const CheckResourceProductAcquisitionDetail: React.FC<{
  data: Array<MgtStoreResourceProductAcquisitionItemAnalyzeDto>;
  resourceName: string;
  searchPeriod: React.MutableRefObject<{ from?: string; until?: string }>;
}> = ({ data, resourceName, searchPeriod }) => {
  const [open, setOpen] = useState<boolean>(false);

  const toggle = useCallback(() => setOpen((prev) => !prev), []);

  const columns = useMemo<
    Array<Column<MgtStoreResourceProductAcquisitionItemAnalyzeDto>>
  >(
    () => [
      {
        accessor: "productName",
        header: "項目",
      },
      {
        accessor: "productCount",
        header: "數量",
      },
      {
        accessor: "productAmount",
        header: "金額(元)",
        formatter: ({ productCount }) => truncateNumber(productCount),
      },
    ],
    []
  );

  return (
    <React.Fragment>
      <button onClick={toggle} type={"button"} className={"text-primary-pink"}>
        查看
      </button>
      <ConditionalFragment condition={open}>
        <Modal className={"md:py-12"}>
          <div
            className={
              "flex flex-col items-stretch w-full max-w-[40rem] h-full bg-grey-5 md:rounded-xl md:overflow-hidden"
            }
          >
            <div className={"flex flex-col px-6 py-4"}>
              <button
                onClick={toggle}
                type={"button"}
                className={"flex items-center justify-center self-end w-6 h-6"}
              >
                <FontAwesomeIcon
                  icon={faTimes}
                  className={"text-2xl text-grey-3"}
                />
              </button>
              <p className={"mb-1 text-2xl font-semibold"}>請領紀錄</p>
              <div className={"md:flex"}>
                <p
                  className={
                    "mb-1 md:mb-0 md:flex-1 text-sm text-grey-2 font-medium"
                  }
                >
                  {resourceName}
                </p>
                <p className={"text-sm text-grey-2 font-medium"}>
                  {formatDate(
                    new Date(searchPeriod.current.from ?? Date.now()),
                    "YYYY/MM/DD"
                  )}{" "}
                  ~{" "}
                  {formatDate(
                    new Date(searchPeriod.current.until ?? Date.now()),
                    "YYYY/MM/DD"
                  )}
                </p>
              </div>
            </div>
            <div className={"flex-1 overflow-scroll"}>
              <div className={"bg-white"}>
                <Header>商品項目</Header>
                <div>
                  <Table showEmpty data={data} columns={columns} />
                </div>
              </div>
            </div>
          </div>
        </Modal>
      </ConditionalFragment>
    </React.Fragment>
  );
};

const connectorForReport = connect((state: RootState) => ({
  loading: state.mrg.loading,
}));
type ReportProps = ConnectedProps<typeof connectorForReport> & {
  storeId: number;
};

const Report = connectorForReport((({ storeId, loading }) => {
  const reportList = useMemo<
    Array<{
      reportType: MgtDownloadGroupStoreSummaryReportReq["reportType"];
      text: string;
    }>
  >(() => {
    return [
      {
        reportType: "ORDER",
        text: "預約紀錄",
      },
      {
        reportType: "VOUCHER",
        text: "票券記錄",
      },
      {
        reportType: "DEPOSIT_POINT",
        text: "儲值金記錄",
      },
      {
        reportType: "BONUS_POINT",
        text: "紅利點數紀錄",
      },
      {
        reportType: "BILL",
        text: "帳單紀錄",
      },
      {
        reportType: "PERFORMANCE_SUMMARY",
        text: "業績統計",
      },
      {
        reportType: "ITEM_SUMMARY",
        text: "項目統計",
      },
      {
        reportType: "PRODUCT_USAGE",
        text: "進銷存貨統計",
      },
    ];
  }, []);

  const { startAt, endAt, setStartAt, setEndAt } = usePeriodSetter();

  const onClick = useCallback(
    async (reportType: MgtDownloadGroupStoreSummaryReportReq["reportType"]) => {
      if (endAt.isBefore(startAt, "day")) {
        alert("結束時間必須小於開始時間。");
      } else {
        await new GetReportHelper({
          startAt,
          endAt,
          reportType,
          storeId,
        }).handleClick();
      }
    },
    [endAt, startAt, storeId]
  );

  return (
    <div>
      <PeriodDataPicker
        className={"mb-5 md:mb-6 p-5 bg-white rounded-xl"}
        onStartDateChange={setStartAt}
        onEndDateChange={setEndAt}
      />

      <ul className={"md:py-6 bg-white rounded-xl"}>
        {reportList.map(({ reportType, text }) => {
          return (
            <li key={text}>
              <p
                className={
                  "px-5 py-3.5 md:px-12 md:py-[1.125rem] text-sm md:text-base text-primary-pink font-semibold border-b border-grey-4"
                }
              >
                {text}
              </p>
              <div
                className={classNames(
                  "flex flex-col md:flex-row md:justify-between md:items-center",
                  "p-5 md:px-12 md:py-[1.125rem]",
                  "border-b border-grey-4"
                )}
              >
                <span
                  className={"text-sm md:text-base text-grey-2 mb-5 md:mb-0"}
                >
                  <span className={"mr-2"}>
                    <FontAwesomeIcon icon={faFileChartLine} />
                  </span>
                  {startAt && formatDate(startAt, "YYYYMMDD")}-
                  {endAt && formatDate(endAt, "YYYYMMDD")}-{text}
                </span>
                <button
                  type="button"
                  onClick={() => onClick(reportType)}
                  disabled={loading}
                  className={classNames(
                    "py-1 px-[2.125rem]",
                    "text-primary-pink hover:text-white",
                    "border border-primary-pink rounded-full",
                    "bg-white hover:bg-primary-pink",
                    "disabled:opacity-50 disabled:cursor-not-allowed"
                  )}
                >
                  下載
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}) as React.FC<ReportProps>);
